import React from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { Card, FormGroup } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";
import Info from "@mui/icons-material/Info";
import { Fields, hasOptionalFieldPermission, Language } from "utils";
import { FormattedDate } from "utils/DateUtils";
import { LocationLink } from "utils/LinkUtils";
import { VoyageInfoFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";

interface VoyageScopeDetailsProps {
	voyage: VoyageInfoFragment;
}

const VoyageScopeDetails = ({ voyage }: VoyageScopeDetailsProps) => {
	const session = useStoreState(store => store.session);
	return (
		<Grid spacing={3} container>
			{(hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn")) && (
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<Card icon={<Info />} title={intl.formatMessage({ id: "ROTATION_SCOPE_IN" })}>
						<FormGroup label={Language("ROTATION_SCOPE_IN_DESIRED_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<FormattedDate date={voyage.scopeIn?.desired} withoutYear />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_IN_PLANNED_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<FormattedDate date={voyage.scopeIn?.planned} withoutYear />
						</FormGroup>

						<FormGroup
							label={Language("ROTATION_SCOPE_IN_EXPECTED_ARRIVAL")}
							labelPlacement="left-center"
							overflow="hidden"
							tooltip={voyage.scopeIn?.systemExpected ? (
								<React.Fragment>
									{`${intl.formatMessage({ id: "SYSTEM" })}: `}
									<FormattedDate date={voyage.scopeIn?.systemExpected} withoutYear />
								</React.Fragment>
							) : undefined}>
							<FormattedDate date={voyage.scopeIn?.expected} withoutYear />
						</FormGroup>

						<FormGroup
							label={Language("ROTATION_SCOPE_IN_ACTUAL_ARRIVAL")}
							labelPlacement="left-center"
							overflow="hidden"
							tooltip={voyage.scopeIn?.systemActual ? (
								<React.Fragment>
									{`${intl.formatMessage({ id: "SYSTEM" })}: `}
									<FormattedDate date={voyage.scopeIn?.systemActual} withoutYear />
								</React.Fragment>
							) : undefined}>
							<FormattedDate date={voyage.scopeIn?.actual} withoutYear />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_EXPECTED")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeIn?.expectedLocation} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_PLANNED")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeIn?.plannedLocation} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_ACTUAL")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeIn?.actualLocation} />
						</FormGroup>
					</Card>
				</Grid>
			)}

			{(hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut")) && (
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<Card icon={<Info />} title={intl.formatMessage({ id: "ROTATION_SCOPE_OUT" })}>
						<FormGroup label={Language("ROTATION_SCOPE_OUT_DESIRED_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<FormattedDate date={voyage.scopeOut?.desired} withoutYear />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_OUT_PLANNED_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<FormattedDate date={voyage.scopeOut?.planned} withoutYear />
						</FormGroup>

						<FormGroup
							label={Language("ROTATION_SCOPE_OUT_EXPECTED_DEPARTURE")}
							labelPlacement="left-center"
							overflow="hidden"
							tooltip={voyage.scopeOut?.systemExpected ? (
								<React.Fragment>
									{`${intl.formatMessage({ id: "SYSTEM" })}: `}
									<FormattedDate date={voyage.scopeOut?.systemExpected} withoutYear />
								</React.Fragment>
							) : undefined}>
							<FormattedDate date={voyage.scopeOut?.expected} withoutYear />
						</FormGroup>

						<FormGroup
							label={Language("ROTATION_SCOPE_OUT_ACTUAL_DEPARTURE")}
							labelPlacement="left-center"
							overflow="hidden"
							tooltip={voyage.scopeOut?.systemActual ? (
								<React.Fragment>
									{`${intl.formatMessage({ id: "SYSTEM" })}: `}
									<FormattedDate date={voyage.scopeOut?.systemActual} withoutYear />
								</React.Fragment>
							) : undefined}>
							<FormattedDate date={voyage.scopeOut?.actual} withoutYear />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_EXPECTED")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeOut?.expectedLocation} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_PLANNED")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeOut?.plannedLocation} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_ACTUAL")} labelPlacement="left-center" overflow="hidden">
							<LocationLink location={voyage.scopeOut?.actualLocation} />
						</FormGroup>
					</Card>
				</Grid>
			)}
		</Grid>
	);
};

export default VoyageScopeDetails;
